/**
 * RecipientEditor — who receives one client's reports.
 *
 * This list is the last thing between a generated file and somebody's inbox, and the two
 * failure modes it has to defend against are not symmetric:
 *
 *  - **Sending to the wrong person** is unrecoverable. A Credgenics report addressed to the
 *    ICICI desk instead of the HDB desk is one lender reading another lender's borrower data,
 *    and no revoke fixes what has already been read. So adding an address names the end lender
 *    and requires the operator to tick that this address should receive *that* lender's data.
 *
 *  - **Sending to nobody** is silent. A client with no active `to` recipient produces a run
 *    that says `sent` and reaches no one, and nobody notices until the client asks where their
 *    report is. So that state is called out at the top of the card, loudly, before it happens.
 *
 * Bounce handling matters for the same reason: SES holds a hard-bounced or complained address
 * on an account-level suppression list, and re-enabling the row here without clearing that
 * would produce accepted-and-dropped mail. The API clears it (recipients.py), reports whether
 * it managed to, and this component surfaces that warning rather than swallowing it.
 *
 * Recipient edits are written immediately — they are not part of the client's draft — so the
 * card says so out loud.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import AddRecipientsDialog from '@/components/AddRecipientsDialog';
import Badge from '@/components/Badge';
import Button from '@/components/Button';
import ConfirmDialog from '@/components/ConfirmDialog';
import EmptyState from '@/components/EmptyState';
import { Checkbox, Field, Input, Select, Switch } from '@/components/Field';
import Modal from '@/components/Modal';
import Table, { type Column } from '@/components/Table';
import { useToast } from '@/components/Toast';
import { isValidEmail } from '@/lib/addresses';
import { api, errorMessage, fetchAllPages, isApiError } from '@/lib/api';
import { istDateTime } from '@/lib/format';
import { RECIPIENT_KINDS, type BounceState, type Recipient, type RecipientKind } from '@/lib/types';

/** The API returns two fields beyond the wire type in lib/types.ts. */
interface RecipientRow extends Recipient {
  /** Server's own verdict: active, not suppressed, not hard-bounced. */
  sendable?: boolean;
  /** Present when re-enabling could not clear the SES suppression entry. */
  warning?: string;
}

const KIND_LABEL: Record<RecipientKind, string> = {
  to: 'To',
  cc: 'Cc',
  bcc: 'Bcc',
};

const KIND_HINT: Record<RecipientKind, string> = {
  to: 'Named on the message. At least one active To is required or nothing is delivered.',
  cc: 'Copied, and visible to everyone else on the message.',
  bcc: 'Copied without the other recipients seeing the address.',
};

const BOUNCE_SPEC: Record<
  BounceState,
  { label: string; tone: 'ok' | 'warn' | 'danger'; glyph: string; title: string; terminal?: boolean }
> = {
  ok: { label: 'Deliverable', tone: 'ok', glyph: '✓', title: 'No bounce or complaint recorded' },
  soft: {
    label: 'Soft bounce',
    tone: 'warn',
    glyph: '◐',
    title: 'A transient failure — full mailbox, greylisting. Still being attempted.',
  },
  hard: {
    label: 'Hard bounce',
    tone: 'danger',
    glyph: '✕',
    title: 'The address does not exist. SES holds it on the account suppression list.',
    terminal: true,
  },
  complained: {
    label: 'Complained',
    tone: 'danger',
    glyph: '⚑',
    title: 'The recipient marked a report as spam. Sending again risks the sending domain.',
    terminal: true,
  },
};

interface FormState {
  email: string;
  name: string;
  kind: RecipientKind;
  active: boolean;
}

const EMPTY_FORM: FormState = { email: '', name: '', kind: 'to', active: true };

export interface RecipientEditorProps {
  clientId: string;
  /** The end lender whose borrower data these addresses will receive. Named in every guard. */
  companyName?: string;
  /** Display name of the client, for copy that needs to tell two prefixes apart. */
  clientName?: string;
  /** Fired after every successful load or mutation, so the parent can show a count. */
  onCountChange?: (counts: { total: number; active: number; deliverableTo: number }) => void;
  disabled?: boolean;
}

export function RecipientEditor({
  clientId,
  companyName,
  clientName,
  onCountChange,
  disabled = false,
}: RecipientEditorProps) {
  const toast = useToast();

  const [rows, setRows] = useState<RecipientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  /** Adding is its own dialog: it takes a paste of many addresses, not one field. */
  const [addOpen, setAddOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RecipientRow | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  /** The exact address the operator ticked the lender confirmation for. Editing the address
   *  invalidates it — otherwise the tick made for one desk would carry over to another. */
  const [ackEmail, setAckEmail] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [removing, setRemoving] = useState<RecipientRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const lender = companyName || clientName || clientId;

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      const items = await fetchAllPages<Recipient>((cursor) =>
        api.recipients.list(clientId, cursor ? { cursor, limit: 100 } : { limit: 100 }),
      );
      setRows(items);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => {
    const active = rows.filter((r) => r.active);
    return {
      total: rows.length,
      active: active.length,
      deliverableTo: active.filter(
        (r) => r.kind === 'to' && !r.suppressed_at && r.bounce_state !== 'hard' && r.bounce_state !== 'complained',
      ).length,
    };
  }, [rows]);

  useEffect(() => {
    onCountChange?.(counts);
  }, [counts, onCountChange]);

  // ---------------------------------------------------------------- form

  const openAdd = (): void => {
    setFormError(null);
    setAddOpen(true);
  };

  const openEdit = (row: RecipientRow): void => {
    setEditing(row);
    setForm({ email: row.email, name: row.name ?? '', kind: row.kind, active: row.active });
    // The stored address was confirmed when it was added; changing it re-asks.
    setAckEmail(row.email);
    setFormError(null);
    setFormOpen(true);
  };

  // Re-pointing an existing row at a different address is the one edit that can hand a
  // lender's report to another desk, so it re-asks. Everything else on the row cannot.
  const emailChanged = editing !== null && form.email.trim().toLowerCase() !== editing.email.toLowerCase();
  const needsAck = emailChanged;
  const acknowledged =
    ackEmail !== null && ackEmail.trim().toLowerCase() === form.email.trim().toLowerCase();

  const submit = async (): Promise<void> => {
    const email = form.email.trim();
    if (!isValidEmail(email)) {
      setFormError('That does not look like an email address.');
      return;
    }
    if (needsAck && !acknowledged) {
      setFormError(`Confirm that ${email} should receive ${lender} borrower data.`);
      return;
    }

    if (!editing) return;

    setSaving(true);
    setFormError(null);
    try {
      const saved: RecipientRow = await api.recipients.patch(editing.recipient_id, {
        email,
        name: form.name.trim(),
        kind: form.kind,
        active: form.active,
      });
      setRows((current) => current.map((r) => (r.recipient_id === saved.recipient_id ? saved : r)));
      if (saved.warning) toast.warn(saved.warning, { duration: 0 });
      else toast.success(`${email} updated.`);
      setFormOpen(false);
      void load();
    } catch (err) {
      // A duplicate address comes back as `recipient_exists` with a sentence naming the
      // client, which is more useful than anything this component could write.
      setFormError(errorMessage(err));
      if (!isApiError(err)) toast.error('Could not save the recipient.');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------- row actions

  const setActive = async (row: RecipientRow, active: boolean): Promise<void> => {
    setBusyId(row.recipient_id);
    try {
      const saved: RecipientRow = await api.recipients.patch(row.recipient_id, { active });
      setRows((current) => current.map((r) => (r.recipient_id === saved.recipient_id ? saved : r)));
      if (saved.warning) toast.warn(saved.warning, { duration: 0 });
      else toast.success(`${row.email} ${active ? 'activated' : 'deactivated'}.`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const clearBounce = async (row: RecipientRow): Promise<void> => {
    setBusyId(row.recipient_id);
    try {
      const saved: RecipientRow = await api.recipients.patch(row.recipient_id, {
        bounce_state: 'ok',
        active: true,
      });
      setRows((current) => current.map((r) => (r.recipient_id === saved.recipient_id ? saved : r)));
      if (saved.warning) {
        toast.warn(saved.warning, { duration: 0 });
      } else {
        toast.success(`${row.email} is deliverable again.`, {
          description: 'The SES suppression entry was cleared as well.',
        });
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row: RecipientRow): Promise<void> => {
    try {
      await api.recipients.remove(row.recipient_id);
      setRows((current) => current.filter((r) => r.recipient_id !== row.recipient_id));
      toast.success(`${row.email} removed from ${clientId}.`);
      setRemoving(null);
    } catch (err) {
      toast.error(errorMessage(err));
      throw err; // keeps the confirm dialog open so the operator can retry
    }
  };

  // ---------------------------------------------------------------- table

  const columns: Column<RecipientRow>[] = [
    {
      key: 'kind',
      header: 'Kind',
      width: '64px',
      nowrap: true,
      // A fact about the row that cannot be wrong and never changes on its own, so a
      // mark rather than a pill competing with the delivery state two columns over.
      render: (row) => (
        <Badge variant="plain" title={KIND_HINT[row.kind]}>
          {KIND_LABEL[row.kind]}
        </Badge>
      ),
    },
    {
      // The only width-less column, so it absorbs the slack. No `.truncate`: an address
      // is the one value on this screen that must never be cut, and neither the address
      // nor the name carried a title to recover it from.
      key: 'email',
      header: 'Address',
      render: (row) => (
        <span className="cluster-tight" style={{ minWidth: 0 }}>
          <span className="mono">{row.email}</span>
          {row.name ? <span className="xs dim">{row.name}</span> : null}
        </span>
      ),
    },
    {
      key: 'delivery',
      header: 'Delivery',
      width: '150px',
      nowrap: true,
      render: (row) => {
        const spec = BOUNCE_SPEC[row.bounce_state] ?? BOUNCE_SPEC.ok;
        // "No bounce recorded" is the default state, not news. Eleven green badges and
        // one red is a wall to find the exception in; the badge and the row's left rule
        // are reserved for soft / hard / complained / suppressed.
        return (
          <div className="cluster-tight">
            {row.bounce_state === 'ok' ? (
              <span className="dim" title={spec.title}>
                —
              </span>
            ) : (
              <Badge tone={spec.tone} glyph={spec.glyph} terminalBad={spec.terminal} title={spec.title}>
                {spec.label}
              </Badge>
            )}
            {row.suppressed_at ? (
              <Badge
                tone="danger"
                glyph="⊘"
                terminalBad
                title={`Suppressed in SES on ${istDateTime(row.suppressed_at)}`}
              >
                suppressed
              </Badge>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'active',
      header: 'Active',
      width: '104px',
      nowrap: true,
      render: (row) => (
        <Switch
          checked={row.active}
          disabled={disabled || busyId === row.recipient_id}
          ariaLabel={`${row.active ? 'Deactivate' : 'Activate'} ${row.email}`}
          label={<span className="sr-only">{row.active ? 'Active' : 'Inactive'}</span>}
          onChange={(next) => void setActive(row, next)}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '224px',
      align: 'right',
      nowrap: true,
      render: (row) => (
        <div className="cluster-tight" style={{ justifyContent: 'flex-end' }}>
          {row.suppressed_at || row.bounce_state === 'hard' || row.bounce_state === 'complained' ? (
            <Button
              size="sm"
              disabled={disabled}
              loading={busyId === row.recipient_id}
              title="Clear the bounce state here and the suppression entry in SES"
              onClick={() => void clearBounce(row)}
            >
              Re-enable
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" disabled={disabled} onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" disabled={disabled} onClick={() => setRemoving(row)}>
            Remove
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="stack">
      {/* A structural fact about the data, on screen on every visit forever — so it is
          stated, not alerted. The words are unchanged (CONTRACT-RECIPIENTS §1); only the
          box and the permanent amber are gone, so the danger callout below it means
          something again. */}
      <p className="small muted" style={{ margin: 0 }}>
        Everyone on this list receives <strong>{lender}</strong> borrower data, under client{' '}
        <code>{clientId}</code>. Two clients can share a prefix and be different lenders — check
        the name above before adding an address.
      </p>

      {!loading && counts.deliverableTo === 0 ? (
        <div className="callout tone-danger">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <span>
            <strong>No deliverable To recipient.</strong> A dispatch for this client will build the
            report and reach nobody. Add an address, or re-enable one that is suppressed.
          </span>
        </div>
      ) : null}

      <Table
        columns={columns}
        rows={rows}
        rowKey={(row) => row.recipient_id}
        loading={loading}
        skeletonRows={3}
        dense
        caption={`Recipients for ${clientId}`}
        rowTone={(row) =>
          !row.active
            ? 'muted'
            : row.suppressed_at || row.bounce_state === 'hard' || row.bounce_state === 'complained'
              ? 'danger'
              : row.bounce_state === 'soft'
                ? 'warn'
                : 'default'
        }
        empty={
          loadError ? (
            <EmptyState
              tone="danger"
              title="Could not load recipients"
              description={loadError}
              action={
                <Button variant="primary" onClick={() => void load()}>
                  Retry
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No recipients yet"
              description={`Nothing will be delivered for ${clientId} until at least one active To address exists.`}
              action={
                <Button variant="primary" disabled={disabled} onClick={openAdd}>
                  Add recipients
                </Button>
              }
            />
          )
        }
      />

      <div className="cluster">
        <Button variant="primary" size="sm" disabled={disabled} onClick={openAdd}>
          Add recipients
        </Button>
        <Button size="sm" variant="ghost" loading={loading} onClick={() => void load()}>
          Refresh
        </Button>
        <span className="grow" />
      </div>

      {/* ------------------------------------------------------------ add */}
      <AddRecipientsDialog
        open={addOpen}
        clientId={clientId}
        companyName={companyName}
        clientName={clientName}
        existing={rows}
        onClose={() => setAddOpen(false)}
        onCreated={(created) => {
          setRows((current) => [...current, ...created]);
          // Re-read rather than trust the append: the server decides the ordering, and a
          // bulk add that collided with an existing row returns fewer rows than were pasted.
          void load();
        }}
      />

      {/* ------------------------------------------------------------ edit */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        dismissible={!saving}
        size="md"
        title="Edit recipient"
        description={`Changes apply to the next dispatch for ${clientId}.`}
        footer={
          <>
            <Button variant="ghost" disabled={saving} onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={saving} onClick={() => void submit()}>
              Save recipient
            </Button>
          </>
        }
      >
        <div className="stack">
          <Field label="Email address" required hint="Exactly as it should appear on the message.">
            {(props) => (
              <Input
                {...props}
                mono
                type="email"
                autoComplete="off"
                spellCheck={false}
                invalid={form.email.trim().length > 0 && !isValidEmail(form.email.trim())}
                value={form.email}
                onChange={(event) => {
                  setForm((f) => ({ ...f, email: event.target.value }));
                  setFormError(null);
                }}
              />
            )}
          </Field>

          <Field label="Name" optional hint="Shown in the greeting; not used for routing.">
            {(props) => (
              <Input
                {...props}
                value={form.name}
                onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
              />
            )}
          </Field>

          <Field label="Kind" hint={KIND_HINT[form.kind]}>
            {(props) => (
              <Select
                {...props}
                value={form.kind}
                onChange={(event) =>
                  setForm((f) => ({ ...f, kind: event.target.value as RecipientKind }))
                }
              >
                {RECIPIENT_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {KIND_LABEL[kind]}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Checkbox
            checked={form.active}
            onChange={(event) => setForm((f) => ({ ...f, active: event.target.checked }))}
            label="Active"
            hint="Inactive recipients stay configured but receive nothing."
          />

          {needsAck ? (
            <div className={`callout tone-${acknowledged ? 'info' : 'warn'}`}>
              <span className="callout-glyph" aria-hidden="true">
                {acknowledged ? 'i' : '▲'}
              </span>
              <Checkbox
                checked={acknowledged}
                onChange={(event) => {
                  setAckEmail(event.target.checked ? form.email.trim() : null);
                  setFormError(null);
                }}
                label={
                  <>
                    This address should receive <strong>{lender}</strong> borrower data
                  </>
                }
                hint={`Client ${clientId}${clientName ? ` — ${clientName}` : ''}. Sending one lender's report to another lender's desk cannot be undone by revoking the link.`}
              />
            </div>
          ) : null}

          {formError ? (
            <div className="callout tone-danger" role="alert">
              <span className="callout-glyph" aria-hidden="true">
                ✕
              </span>
              <span>{formError}</span>
            </div>
          ) : null}
        </div>
      </Modal>

      {/* ------------------------------------------------------------ remove */}
      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title="Remove this recipient?"
        confirmLabel="Remove recipient"
        confirmPhrase={removing?.email}
        consequence={`${removing?.email ?? 'This address'} stops receiving reports for ${clientId} from the next dispatch. Links already in their inbox keep working until they expire — revoke the grant on the run if that matters.`}
        onCancel={() => setRemoving(null)}
        onConfirm={() => (removing ? remove(removing) : undefined)}
      >
        <p className="small">
          Removing is not the same as deactivating. If this is a temporary absence, switch{' '}
          <strong>Active</strong> off instead and the configuration survives.
        </p>
      </ConfirmDialog>
    </div>
  );
}

export default RecipientEditor;
